import { RankDetail } from '../../../../apis/ranking/ranking-api.def.ts'
import { ColDef } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { useMemo, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { OVERALL_RANKING_COLUMN_STATE_KEY } from '../../../../utils/constants.ts'
import { BookNameCellRenderer } from '../../../components/book-name-cell-renderer.tsx'
import {
  coloredNumberCellStyle,
  currencyValueFormatter,
} from '../../../../utils/formatters.ts'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../redux/hooks.ts'
import { selectRankingInput } from '../../../../redux/slices/ranking-input.slice.ts'
import { RankColumn } from './rank-column.tsx'

interface OverallRankingTableProps {
  isLoading: boolean
  isFetching: boolean
  rankingList: RankDetail[]
  groupByFirm: boolean
  height: number
}

export const OverallRankingTable: React.FC<OverallRankingTableProps> = ({
  isLoading,
  isFetching,
  rankingList,
  groupByFirm,
  height,
}) => {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact>(null)
  const rankingInput = useAppSelector(selectRankingInput)

  const columns = useMemo<ColDef[]>(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: t('overall_ranking.ranking_table.columns.rank'),
        field: 'rank',
        cellRenderer: RankColumn,
        type: 'textColumn',
      },
      {
        headerName: groupByFirm
          ? t('overall_ranking.ranking_table.columns.firm_name')
          : t('overall_ranking.ranking_table.columns.book_name'),
        field: groupByFirm ? 'firmName' : 'bookName',
        type: 'textColumn',
        cellRenderer: BookNameCellRenderer,
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.ideas_count'),
        field: 'ideasCount',
        type: 'numberColumn',
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.new_ideas_count'),
        field: 'newIdeasCount',
        type: 'numberColumn',
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.daily_pnl'),
        field: 'dailyPnl',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.total_pnl'),
        field: 'adjustedPnl',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      },
      {
        headerName: t('overall_ranking.ranking_table.columns.category'),
        field: 'category',
        type: 'textColumn',
      },
    ]

    // If you want to add Avg PnL column for groupByFirm
    if (groupByFirm) {
      const totalPnlIndex = baseColumns.findIndex(
        (col) => col.field === 'totalPnl'
      )
      baseColumns.splice(totalPnlIndex, 0, {
        headerName: 'Avg PnL',
        field: 'averageAdjustedPnl',
        type: 'numberColumn',
        cellStyle: coloredNumberCellStyle,
        valueFormatter: (params) =>
          currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
      })
    }

    // If you want to add a separate Firm Name column when not grouping by firm
    if (!groupByFirm) {
      const exists = baseColumns.some((col) => col.field === 'firmName')
      if (!exists) {
        baseColumns.push({
          headerName: t('overall_ranking.ranking_table.columns.firm_name'),
          field: 'firmName',
          type: 'textColumn',
        })
      }
    }

    const savedState = localStorage.getItem(OVERALL_RANKING_COLUMN_STATE_KEY)
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        const colOrder = state.map((col: { colId: string }) => col.colId)
        const ordered = colOrder
          .map((id: string) => baseColumns.find((col) => col.field === id))
          .filter(Boolean)
        const missing = baseColumns.filter(
          (col) => !colOrder.includes(col.field)
        )
        return [...ordered, ...missing]
      } catch {
        return baseColumns
      }
    }

    return baseColumns
  }, [t, groupByFirm, rankingInput])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      minWidth: 200,
      resizable: true,
      filter: false,
    }),
    []
  )

  const handleGridReady = () => {
    if (!gridRef.current) return
    const savedState = localStorage.getItem(OVERALL_RANKING_COLUMN_STATE_KEY)
    if (savedState && gridRef.current.api) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
      gridRef.current.api.sizeColumnsToFit()
    }
  }

  const handleColumnChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      OVERALL_RANKING_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleSortChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      OVERALL_RANKING_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columns={columns}
      defaultColDef={defaultColDef}
      rowData={rankingList}
      loading={isLoading || isFetching}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onSortChanged={handleSortChanged}
      height={height}
    />
  )
}
